import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Box,
    Divider,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Popover,
    Chip,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AddIcon from '@material-ui/icons/Add';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import { Field, Form, Formik } from 'formik';
import { Autocomplete } from '@material-ui/lab';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import BuildIcon from '@material-ui/icons/Build';
import SearchIcon from '@material-ui/icons/Search';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { format } from 'date-fns';
import useApi from '../../../utils/useApi';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import ClientCreateModal from '../../Client/ClientCreateModal';
import { formattedCurrency } from '../../../utils/utils';
import StoreContext from '../../Store/context';
import * as Yup from 'yup';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';

const validationSchema = Yup.object().shape({
    customer: Yup.object().required('Cliente é obrigatório').nullable(),
    scheduleDate: Yup.date().required('Data e hora são obrigatórias').nullable(),
    totalValue: Yup.number().min(0, 'Valor inválido').typeError('Deve ser um número'),
    products: Yup.object().test('at-least-one', 'Adicione pelo menos um serviço ou produto', function (value) {
        const { services } = this.parent;
        const hasProducts = value && Object.keys(value).length > 0;
        const hasServices = services && Object.keys(services).length > 0;
        return hasProducts || hasServices;
    }),
});

const CashSessionWatcher = ({ setFieldValue, cashSession }) => {
    useEffect(() => {
        if (cashSession) {
            setFieldValue('cashRegister', cashSession);
        }
    }, [cashSession, setFieldValue]);
    return null;
};

const ResponsibleWatcher = ({ setFieldValue, userInfo, credencials, currentResponsible }) => {
    useEffect(() => {
        if (!currentResponsible && userInfo?.data && credencials) {
            const user = userInfo.data.find(u => String(u.id) === String(credencials.id));
            if (user) {
                setFieldValue('responsible', user);
            }
        }
    }, [userInfo, credencials, currentResponsible, setFieldValue]);
    return null;
};

const ScheduleNew = ({ open, onClose, initialDate, onSuccess }) => {
    const { credencials, setModalCashierSelect } = useContext(StoreContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalClient, setModalClient] = useState(false);
    const [listItens, setListItens] = useState([]);

    // Popover State
    const [anchorEl, setAnchorEl] = useState(null);
    const [timeQuantity, setTimeQuantity] = useState(1);
    const [timeUnit, setTimeUnit] = useState('minutes'); // 'minutes' | 'hours'

    // Alert State
    const [alert, setAlert] = useState(false);

    // Item Selection Tab State
    const [itemTab, setItemTab] = useState(0); // 0 = Services, 1 = Products
    const [itemSearch, setItemSearch] = useState(''); // Search filter for items
    const [attendantSearch, setAttendantSearch] = useState(''); // Search filter for attendants

    // Recent Estimated Times from localStorage
    const [recentTimes, setRecentTimes] = useState(() => {
        try {
            const stored = localStorage.getItem('recentEstimatedTimes');
            return stored ? JSON.parse(stored) : [30, 60, 90, 120];
        } catch {
            return [30, 60, 90, 120];
        }
    });

    const saveRecentTime = (time) => {
        const newTimes = [time, ...recentTimes.filter(t => t !== time)].slice(0, 5);
        setRecentTimes(newTimes);
        localStorage.setItem('recentEstimatedTimes', JSON.stringify(newTimes));
    };

    const [registerLoad, registerInfo, setRegisterInfo] = useApi({
        method: 'post',
        url: '/schedules',
    });

    const [clientLoad, clientInfo] = useApi({
        method: 'get',
        url: '/customers',
    });

    const [searchProductLoad, productsInfo] = useApi({
        method: 'get',
        url: '/products',
    });

    const [searchServicesLoad, servicesInfo] = useApi({
        method: 'get',
        url: '/services',
    });

    const [userLoad, userInfo] = useApi({
        method: 'get',
        url: '/users',
    });

    const [promotionLoad, promotionInfo] = useApi({
        method: 'get',
        url: '/promotions',
        params: { isActive: 1, isExpired: 0 },
    });

    useEffect(() => {
        if (open) {
            clientLoad();
            searchProductLoad();
            searchServicesLoad();
            promotionLoad();
            userLoad();
            setListItens([]);
        }
    }, [open]);

    const initialResponsible = useMemo(() => {
        if (userInfo.data && credencials) {
            return userInfo.data.find(u => String(u.id) === String(credencials.id)) || null;
        }
        return null;
    }, [userInfo.data, credencials]);

    const calculateTotal = (items, discount = 0) => {
        const itemsTotal = items.reduce((acc, curr) => acc + (curr.value * curr.amount), 0);
        return Math.max(0, itemsTotal - discount);
    };

    const handleAddItem = (item, type, setFieldValue, currentValues) => {
        const newItem = {
            id: item.id,
            name: item.name,
            value: item.value || 0,
            type,
            amount: 1,
        };

        const newList = [...listItens, newItem];
        setListItens(newList);

        const newTotal = calculateTotal(newList, currentValues.discount);
        setFieldValue('totalValue', newTotal);

        const typeKey = type === 'product' ? 'products' : 'services';
        const updatedItems = { ...currentValues[typeKey] };
        updatedItems[item.id] = (updatedItems[item.id] || 0) + 1;
        setFieldValue(typeKey, updatedItems);
    };

    const handleRemoveItem = (index, setFieldValue, currentValues) => {
        const itemToRemove = listItens[index];
        const newList = listItens.filter((_, i) => i !== index);
        setListItens(newList);

        const newTotal = calculateTotal(newList, currentValues.discount);
        setFieldValue('totalValue', newTotal);

        const typeKey = itemToRemove.type === 'product' ? 'products' : 'services';
        const updatedItems = { ...currentValues[typeKey] };
        if (updatedItems[itemToRemove.id] > 1) {
            updatedItems[itemToRemove.id] -= 1;
        } else {
            delete updatedItems[itemToRemove.id];
        }
        setFieldValue(typeKey, updatedItems);
    };

    const handleDiscountChange = (val, setFieldValue, currentValues) => {
        const itemsTotal = calculateTotal(listItens, 0);
        const validDiscount = Math.max(0, Math.min(val, itemsTotal));
        setFieldValue('discount', validDiscount);
        setFieldValue('totalValue', calculateTotal(listItens, validDiscount));
    };

    // --- Time Popover Logic ---

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const handleAddTime = (setFieldValue, currentEstimation) => {
        const addedMinutes = timeUnit === 'hours' ? Number(timeQuantity) * 60 : Number(timeQuantity);
        const newEstimation = (currentEstimation || 0) + addedMinutes;
        setFieldValue('estimatedTime', newEstimation);
    };

    const handleResetTime = (setFieldValue) => {
        setFieldValue('estimatedTime', 0);
    };

    const formatEstimatedTime = (minutes) => {
        if (!minutes) return '0 min';
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    const onSubmit = (values, { setSubmitting }) => {
        if (values.downPayment > 0 && !credencials.cashSession) {
            setSubmitting(false);
            setModalCashierSelect(true);
            return;
        }

        const data = {
            customer: values.customer.id,
            products: values.products,
            services: values.services,
            totalValue: values.totalValue,
            discount: values.discount,
            promotion: values.promotion?.id,
            downPayment: values.downPayment,
            scheduleDate: format(values.scheduleDate, 'yyyy-MM-dd HH:mm:ss'),
            observation: values.observation,
            cashRegister: credencials?.cashSession,
            paymentMethod: values.paymentMethod,
            responsibleId: values.responsible?.id,
            estimatedTime: values.estimatedTime,
            userId: credencials?.id || 1,
        };
        registerLoad({
            data,
            onCompleted: (res) => {
                setSubmitting(false);
                if (res && res.data) {
                    saveRecentTime(values.estimatedTime);
                    onSuccess && onSuccess();
                    onClose();
                }
            },
            onError: (err) => {
                setSubmitting(false);
                setAlert(true);
            }
        });
    };

    const initialDateMemo = useMemo(() => initialDate || new Date(), [initialDate]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth="md"
            PaperProps={{
                style: {
                    borderRadius: isMobile ? 0 : 16,
                    overflow: 'hidden',
                }
            }}
        >
            <Formik
                initialValues={{
                    customer: null,
                    products: {},
                    services: {},
                    totalValue: 0,
                    discount: 0,
                    promotion: null,
                    downPayment: 0,
                    scheduleDate: initialDateMemo,
                    observation: '',
                    paymentMethod: 4,
                    responsible: initialResponsible,
                    estimatedTime: 60,
                }}
                key={initialDateMemo.toString()}
                onSubmit={onSubmit}
                validationSchema={validationSchema}
            >
                {({ setFieldValue, values, errors, touched, isSubmitting }) => {
                    if (Object.keys(errors).length > 0) console.log('Formik Errors:', errors);
                    return (
                        <Form>
                            <CashSessionWatcher setFieldValue={setFieldValue} cashSession={credencials?.cashSession} />
                            <ResponsibleWatcher
                                setFieldValue={setFieldValue}
                                userInfo={userInfo}
                                credencials={credencials}
                                currentResponsible={values.responsible}
                            />
                            <DialogTitle
                                style={{
                                    background: 'linear-gradient(135deg, #97cbfd 0%, #97ACFC 100%)',
                                    color: '#182F48',
                                    padding: isMobile ? '16px' : '20px 24px',
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems={isMobile ? 'flex-start' : 'center'}
                                    justifyContent="space-between"
                                    flexDirection={isMobile ? 'column' : 'row'}
                                    style={{ gap: isMobile ? 12 : 0 }}
                                >
                                    <Box display="flex" alignItems="center">
                                        {!isMobile && (
                                            <Box
                                                mr={2}
                                                p={1}
                                                borderRadius={12}
                                                style={{ background: 'rgba(255,255,255,0.3)', display: 'flex' }}
                                            >
                                                <AddIcon style={{ fontSize: 24 }} />
                                            </Box>
                                        )}
                                        <Box>
                                            <Typography variant={isMobile ? 'subtitle1' : 'h6'} style={{ fontWeight: 700, letterSpacing: '-0.3px' }}>
                                                {values.customer
                                                    ? `Agendamento para ${values.customer.name}`
                                                    : 'Novo Agendamento'}
                                            </Typography>
                                            {!isMobile && (
                                                <Typography variant="caption" style={{ opacity: 0.8, fontWeight: 500 }}>
                                                    {values.customer
                                                        ? 'Preencha os detalhes do atendimento'
                                                        : 'Selecione o cliente para continuar'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Date/Time Picker - Prominent in Header */}
                                    {values.customer && (
                                        <Box
                                            style={{
                                                background: 'rgba(255,255,255,0.9)',
                                                borderRadius: 12,
                                                padding: isMobile ? '6px 12px' : '8px 16px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                width: isMobile ? '100%' : 'auto',
                                                justifyContent: isMobile ? 'center' : 'flex-start',
                                            }}
                                        >
                                            <KeyboardDateTimePicker
                                                format={isMobile ? 'dd/MM HH:mm' : 'dd/MM/yyyy HH:mm'}
                                                value={values.scheduleDate}
                                                onChange={(date) => setFieldValue('scheduleDate', date)}
                                                cancelLabel="Cancelar"
                                                ampm={false}
                                                InputProps={{
                                                    disableUnderline: true,
                                                    style: {
                                                        fontWeight: 700,
                                                        fontSize: isMobile ? '1rem' : '1.1rem',
                                                        color: '#182F48',
                                                    },
                                                }}
                                                style={{ width: isMobile ? '100%' : 200 }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </DialogTitle>
                            <DialogContent
                                dividers
                                style={{
                                    maxHeight: isMobile ? 'calc(100vh - 280px)' : '60vh',
                                    overflowY: 'auto',
                                    padding: isMobile ? 12 : 24,
                                }}
                            >
                                <Grid container spacing={isMobile ? 1 : 2}>
                                    {!values.customer ? (
                                        <Grid item xs={12}>
                                            <Box
                                                p={isMobile ? 2 : 3}
                                                borderRadius={16}
                                                style={{
                                                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                                    border: errors.customer && touched.customer ? '2px solid #ef4444' : '2px dashed #9fa8da',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    style={{ fontWeight: 700, color: '#182F48', marginBottom: 8 }}
                                                >
                                                    👤 Selecione o Cliente
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: '#64748b', marginBottom: 16 }}
                                                >
                                                    Comece digitando o nome do cliente para continuar
                                                </Typography>
                                                <Autocomplete
                                                    disableClearable
                                                    options={clientInfo.data || []}
                                                    getOptionLabel={(option) => `${option.name} ${option.lastName || ''}`}
                                                    value={values.customer}
                                                    onChange={(ev, val) => setFieldValue('customer', val)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder="Digite o nome do cliente..."
                                                            variant="outlined"
                                                            error={Boolean(errors.customer && touched.customer)}
                                                            helperText={touched.customer && errors.customer}
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                style: {
                                                                    background: '#fff',
                                                                    borderRadius: 12,
                                                                },
                                                                endAdornment: (
                                                                    <>
                                                                        {params.InputProps.endAdornment}
                                                                        <InputAdornment position="end">
                                                                            <IconButton onClick={() => setModalClient(true)} size="small" title="Novo Cliente">
                                                                                <PersonAddIcon color="primary" />
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Grid>
                                    ) : null}

                                    {values.customer && (
                                        <>
                                            {/* Attendant & Time Section - Redesigned */}
                                            <Grid item xs={12}>
                                                <Box
                                                    p={isMobile ? 1.5 : 2}
                                                    borderRadius={isMobile ? 8 : 12}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                        border: '1px solid #9fa8da',
                                                    }}
                                                >
                                                    <Grid container spacing={isMobile ? 1 : 2}>
                                                        {/* Attendant Selection */}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{ fontWeight: 700, color: '#182F48', textTransform: 'uppercase', letterSpacing: 1 }}
                                                            >
                                                                👤 Atendente Responsável
                                                            </Typography>
                                                            <TextField
                                                                placeholder="Buscar atendente..."
                                                                value={attendantSearch}
                                                                onChange={(e) => setAttendantSearch(e.target.value)}
                                                                size="small"
                                                                fullWidth
                                                                style={{ marginTop: 8, marginBottom: 8 }}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <SearchIcon fontSize="small" style={{ color: '#9fa8da' }} />
                                                                        </InputAdornment>
                                                                    ),
                                                                    style: {
                                                                        borderRadius: 10,
                                                                        background: '#fff',
                                                                    }
                                                                }}
                                                            />
                                                            <Box
                                                                display="flex"
                                                                flexWrap="wrap"
                                                                style={{ gap: 8 }}
                                                            >
                                                                {(userInfo.data || [])
                                                                    .filter(user => `${user.name} ${user.lastName || ''}`.toLowerCase().includes(attendantSearch.toLowerCase()))
                                                                    .slice(0, 10)
                                                                    .map(user => (
                                                                        <Chip
                                                                            key={user.id}
                                                                            label={`${user.name} ${user.lastName || ''}`}
                                                                            onClick={() => setFieldValue('responsible', user)}
                                                                            style={{
                                                                                background: values.responsible?.id === user.id
                                                                                    ? 'linear-gradient(135deg, #17497F 0%, #303f9f 100%)'
                                                                                    : '#fff',
                                                                                color: values.responsible?.id === user.id ? '#fff' : '#182F48',
                                                                                fontWeight: 600,
                                                                                cursor: 'pointer',
                                                                                border: values.responsible?.id === user.id ? 'none' : '1px solid #9fa8da',
                                                                                boxShadow: values.responsible?.id === user.id ? '0 2px 8px rgba(63, 81, 181, 0.3)' : 'none',
                                                                            }}
                                                                            clickable
                                                                        />
                                                                    ))}
                                                                {(userInfo.data || []).filter(user => `${user.name} ${user.lastName || ''}`.toLowerCase().includes(attendantSearch.toLowerCase())).length === 0 && (
                                                                    <Typography variant="body2" color="textSecondary">
                                                                        {attendantSearch ? 'Nenhum atendente encontrado' : 'Nenhum atendente cadastrado'}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Grid>

                                                        {/* Estimated Time with Shortcuts */}
                                                        <Grid item xs={12} md={6}>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{ fontWeight: 700, color: '#182F48', textTransform: 'uppercase', letterSpacing: 1 }}
                                                                >
                                                                    ⏱️ Tempo Estimado
                                                                </Typography>
                                                                <Box
                                                                    onClick={handlePopoverOpen}
                                                                    style={{
                                                                        background: 'linear-gradient(135deg, #17497F 0%, #303f9f 100%)',
                                                                        borderRadius: 8,
                                                                        padding: '4px 12px',
                                                                        cursor: 'pointer',
                                                                        boxShadow: '0 2px 8px rgba(63, 81, 181, 0.3)',
                                                                    }}
                                                                >
                                                                    <Typography variant="subtitle1" style={{ color: '#fff', fontWeight: 700 }}>
                                                                        {formatEstimatedTime(values.estimatedTime)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>

                                                            {/* Time Shortcuts */}
                                                            <Box
                                                                display="flex"
                                                                flexWrap="wrap"
                                                                style={{ gap: 8, marginTop: 8 }}
                                                            >
                                                                <Typography variant="caption" style={{ color: '#182F48', fontWeight: 500, width: '100%' }}>
                                                                    Atalhos recentes:
                                                                </Typography>
                                                                {recentTimes.map((time, idx) => (
                                                                    <Chip
                                                                        key={`time-${idx}`}
                                                                        label={formatEstimatedTime(time)}
                                                                        onClick={() => setFieldValue('estimatedTime', time)}
                                                                        size="small"
                                                                        style={{
                                                                            background: values.estimatedTime === time
                                                                                ? 'linear-gradient(135deg, #17497F 0%, #303f9f 100%)'
                                                                                : '#fff',
                                                                            color: values.estimatedTime === time ? '#fff' : '#182F48',
                                                                            fontWeight: 600,
                                                                            cursor: 'pointer',
                                                                            border: values.estimatedTime === time ? 'none' : '1px solid #9fa8da',
                                                                        }}
                                                                        clickable
                                                                    />
                                                                ))}
                                                                <Chip
                                                                    label="+ Personalizar"
                                                                    onClick={handlePopoverOpen}
                                                                    size="small"
                                                                    style={{
                                                                        background: 'transparent',
                                                                        color: '#182F48',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        border: '1px dashed #17497F',
                                                                    }}
                                                                    clickable
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>

                                                {/* Time Popover */}
                                                <Popover
                                                    open={Boolean(anchorEl)}
                                                    anchorEl={anchorEl}
                                                    onClose={handlePopoverClose}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'center',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'center',
                                                    }}
                                                >
                                                    <Box p={2} width={300}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" align="center" gutterBottom>
                                                                    Adicionar Tempo
                                                                </Typography>
                                                                <Divider />
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <TextField
                                                                    label="Qtd"
                                                                    type="number"
                                                                    value={timeQuantity}
                                                                    onChange={(e) => setTimeQuantity(e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                    inputProps={{ min: 0 }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <TextField
                                                                    select
                                                                    label="Unidade"
                                                                    value={timeUnit}
                                                                    onChange={(e) => setTimeUnit(e.target.value)}
                                                                    fullWidth
                                                                    size="small"
                                                                >
                                                                    <MenuItem value="minutes">Minutos</MenuItem>
                                                                    <MenuItem value="hours">Horas</MenuItem>
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} container justify="space-between">
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => handleResetTime(setFieldValue)}
                                                                    startIcon={<RotateLeftIcon />}
                                                                >
                                                                    Resetar
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => handleAddTime(setFieldValue, values.estimatedTime)}
                                                                    startIcon={<AddIcon />}
                                                                >
                                                                    Adicionar
                                                                </Button>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" display="block" align="center">
                                                                    Total atual: <strong>{formatEstimatedTime(values.estimatedTime)}</strong>
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Popover>
                                            </Grid>

                                            {/* Items Section - Redesigned */}
                                            <Grid item xs={12}>
                                                <Box
                                                    mt={isMobile ? 1 : 2}
                                                    p={isMobile ? 1.5 : 2}
                                                    borderRadius={isMobile ? 8 : 12}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                        border: errors.products ? '2px solid #ef4444' : '1px solid #e2e8f0',
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            style={{ fontWeight: 700, color: errors.products ? '#ef4444' : '#182F48' }}
                                                        >
                                                            Serviços & Produtos
                                                        </Typography>
                                                        {errors.products && (
                                                            <Typography variant="caption" style={{ color: '#ef4444' }}>
                                                                {errors.products}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {/* Tabs for Services/Products */}
                                                    <Tabs
                                                        value={itemTab}
                                                        onChange={(e, newVal) => setItemTab(newVal)}
                                                        indicatorColor="primary"
                                                        textColor="primary"
                                                        variant="fullWidth"
                                                        style={{
                                                            background: '#fff',
                                                            borderRadius: 10,
                                                            marginBottom: 16,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                        }}
                                                    >
                                                        <Tab
                                                            icon={<BuildIcon fontSize="small" />}
                                                            label="Serviços"
                                                            style={{ textTransform: 'none', fontWeight: 600 }}
                                                        />
                                                        <Tab
                                                            icon={<ShoppingCartIcon fontSize="small" />}
                                                            label="Produtos"
                                                            style={{ textTransform: 'none', fontWeight: 600 }}
                                                        />
                                                    </Tabs>

                                                    {/* Search Field */}
                                                    <TextField
                                                        placeholder={itemTab === 0 ? "Buscar serviço..." : "Buscar produto..."}
                                                        value={itemSearch}
                                                        onChange={(e) => setItemSearch(e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        style={{ marginBottom: 12 }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
                                                                </InputAdornment>
                                                            ),
                                                            style: {
                                                                borderRadius: 10,
                                                                background: '#fff'
                                                            }
                                                        }}
                                                    />

                                                    {/* Clickable Items */}
                                                    <Box
                                                        display="flex"
                                                        flexWrap="wrap"
                                                        style={{ gap: 8, maxHeight: 120, overflowY: 'auto', padding: 4 }}
                                                    >
                                                        {itemTab === 0 && (servicesInfo.data || [])
                                                            .filter(item => item.name.toLowerCase().includes(itemSearch.toLowerCase()))
                                                            .slice(0, 10)
                                                            .map(item => (
                                                                <Chip
                                                                    key={`service-${item.id}`}
                                                                    label={`${item.name} • ${formattedCurrency(item.value || 0)}`}
                                                                    onClick={() => handleAddItem(item, 'service', setFieldValue, values)}
                                                                    icon={<AddIcon fontSize="small" />}
                                                                    style={{
                                                                        background: 'linear-gradient(135deg, #97cbfd 0%, #97ACFC 100%)',
                                                                        color: '#182F48',
                                                                        fontWeight: 500,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                    clickable
                                                                />
                                                            ))}
                                                        {itemTab === 1 && (productsInfo.data || [])
                                                            .filter(item => item.name.toLowerCase().includes(itemSearch.toLowerCase()))
                                                            .slice(0, 10)
                                                            .map(item => (
                                                                <Chip
                                                                    key={`product-${item.id}`}
                                                                    label={`${item.name} • ${formattedCurrency(item.value || 0)}`}
                                                                    onClick={() => handleAddItem(item, 'product', setFieldValue, values)}
                                                                    icon={<AddIcon fontSize="small" />}
                                                                    style={{
                                                                        background: 'linear-gradient(135deg, #97cbfd 0%, #97ACFC 100%)',
                                                                        color: '#182F48',
                                                                        fontWeight: 500,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                    clickable
                                                                />
                                                            ))}
                                                        {itemTab === 0 && (servicesInfo.data || []).filter(item => item.name.toLowerCase().includes(itemSearch.toLowerCase())).length === 0 && (
                                                            <Typography variant="body2" color="textSecondary">
                                                                {itemSearch ? 'Nenhum serviço encontrado' : 'Nenhum serviço cadastrado'}
                                                            </Typography>
                                                        )}
                                                        {itemTab === 1 && (productsInfo.data || []).filter(item => item.name.toLowerCase().includes(itemSearch.toLowerCase())).length === 0 && (
                                                            <Typography variant="body2" color="textSecondary">
                                                                {itemSearch ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {/* Selected Items */}
                                                    {listItens.length > 0 && (
                                                        <Box mt={2}>
                                                            <Typography variant="caption" style={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                                Itens Selecionados ({listItens.length})
                                                            </Typography>
                                                            <Box
                                                                display="flex"
                                                                flexWrap="wrap"
                                                                style={{ gap: 8, marginTop: 8 }}
                                                            >
                                                                {listItens.map((item, index) => (
                                                                    <Chip
                                                                        key={`selected-${index}`}
                                                                        label={`${item.name} • ${formattedCurrency(item.value)}`}
                                                                        onDelete={() => handleRemoveItem(index, setFieldValue, values)}
                                                                        style={{
                                                                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                                                            color: '#fff',
                                                                            fontWeight: 500,
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Grid>

                                            {/* Financial Section - Redesigned */}
                                            <Grid item xs={12}>
                                                <Box
                                                    mt={isMobile ? 1 : 2}
                                                    p={isMobile ? 1.5 : 2}
                                                    borderRadius={isMobile ? 8 : 12}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                                        border: '1px solid #e2e8f0',
                                                    }}
                                                >
                                                    <Typography
                                                        variant={isMobile ? 'subtitle2' : 'subtitle1'}
                                                        style={{ fontWeight: 700, color: '#182F48', marginBottom: isMobile ? 12 : 16 }}
                                                    >
                                                        💰 Financeiro
                                                    </Typography>

                                                    <Grid container spacing={isMobile ? 1 : 2}>
                                                        <Grid item xs={6} md={4}>
                                                            <Autocomplete
                                                                options={promotionInfo.data || []}
                                                                getOptionLabel={(option) => option.name}
                                                                value={values.promotion}
                                                                onChange={(ev, val) => setFieldValue('promotion', val)}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Promoção"
                                                                        size="small"
                                                                        fullWidth
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6} md={2}>
                                                            <CurrencyTextField
                                                                label="Desconto"
                                                                value={values.discount}
                                                                onChange={(ev, val) => handleDiscountChange(val, setFieldValue, values)}
                                                                currencySymbol="R$"
                                                                decimalCharacter=","
                                                                digitGroupSeparator="."
                                                                fullWidth
                                                                size="small"
                                                                inputProps={{ min: 0 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6} md={2}>
                                                            <CurrencyTextField
                                                                label="Entrada"
                                                                value={values.downPayment}
                                                                onChange={(ev, val) => {
                                                                    const validPayment = Math.max(0, Math.min(val, values.totalValue));
                                                                    setFieldValue('downPayment', validPayment);
                                                                }}
                                                                currencySymbol="R$"
                                                                decimalCharacter=","
                                                                digitGroupSeparator="."
                                                                fullWidth
                                                                size="small"
                                                                inputProps={{ min: 0 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Box
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #e8f4fc 0%, #d4e8f7 100%)',
                                                                    borderRadius: 10,
                                                                    padding: '12px 16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    border: '1px solid #b8d4e8',
                                                                }}
                                                            >
                                                                <Typography variant="caption" style={{ color: '#182F48', fontWeight: 600 }}>
                                                                    Total
                                                                </Typography>
                                                                <Typography variant="h6" style={{ color: '#182F48', fontWeight: 700 }}>
                                                                    {formattedCurrency(values.totalValue)}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>

                                            {values.downPayment > 0 && (
                                                <>
                                                    <Grid item xs={12} md={8}>
                                                        <TextField
                                                            select
                                                            label="Método de Pagamento"
                                                            value={values.paymentMethod}
                                                            onChange={(ev) => setFieldValue('paymentMethod', ev.target.value)}
                                                            fullWidth
                                                        >
                                                            <MenuItem value={4}>Dinheiro</MenuItem>
                                                            <MenuItem value={3}>PIX</MenuItem>
                                                            <MenuItem value={1}>Crédito</MenuItem>
                                                            <MenuItem value={2}>Débito</MenuItem>
                                                        </TextField>
                                                    </Grid>
                                                </>
                                            )}

                                            <Grid item xs={12}>
                                                <TextField
                                                    name="observation"
                                                    label="Observações"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    value={values.observation}
                                                    onChange={(ev) => setFieldValue('observation', ev.target.value)}
                                                    variant="outlined"
                                                    style={{ marginTop: 16 }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </DialogContent>
                            <DialogActions
                                style={{
                                    padding: isMobile ? '12px 16px' : '16px 24px',
                                    background: '#fafbfc',
                                    flexDirection: isMobile ? 'column-reverse' : 'row',
                                    gap: isMobile ? 8 : 0,
                                }}
                            >
                                <Button
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                    fullWidth={isMobile}
                                    style={{
                                        borderRadius: 10,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#64748b',
                                        padding: isMobile ? '12px' : undefined,
                                    }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth={isMobile}
                                    disabled={isSubmitting || registerInfo.loading}
                                    startIcon={registerInfo.loading && <CircularProgress size={20} color="inherit" />}
                                    style={{
                                        background: '#182F48',
                                        color: '#fff',
                                        borderRadius: 10,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        padding: isMobile ? '14px 24px' : '10px 24px',
                                        boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                    }}
                                >
                                    Salvar Agendamento
                                </Button>
                            </DialogActions>

                            <ClientCreateModal
                                open={modalClient}
                                onClose={() => setModalClient(false)}
                                onSuccess={(newClient) => {
                                    clientLoad();
                                    setFieldValue('customer', newClient);
                                    setModalClient(false);
                                }}
                            />
                        </Form>
                    );
                }}
            </Formik>
            <UIDialogConfirm
                open={alert}
                close={() => setAlert(false)}
                data={registerInfo}
                messageError={registerInfo.error && registerInfo.error.response && registerInfo.error.response.data && registerInfo.error.response.data.error}
                type="error"
            />
        </Dialog >
    );
};

export default ScheduleNew;
